// src/components/PackageCard.js
//
// One course package, presented for a customer to compare and choose.
//
// Props:
//   pkg       — a COURSE_PACKAGES entry (config/coursePackages.js)
//   selected  — bool, draws the selected border + check
//   onSelect  — (packageId) => void. OMIT for a non-interactive card
//               (e.g. the single locked package on an adult account).
//
// Currently used by /enroll only. SelectPlan.js and CourseRenewal.js still
// hand-render their own package cards inline; retrofitting them onto this
// component is on the Stage 7 list (they're live pages — not this stage).
import React from 'react';
import { Users, User, Check, Crown, ChevronDown } from 'lucide-react';

function PackageCard({ pkg, selected = false, onSelect }) {
  if (!pkg) return null;
  const interactive = typeof onSelect === 'function';
  const Icon = pkg.type === 'group' ? Users : User;

  const pick = () => { if (interactive) onSelect(pkg.id); };

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      onClick={pick}
      onKeyDown={interactive
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }
        : undefined}
      className={`relative rounded-2xl border-2 p-4 transition-all ${
        selected ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-white'
      } ${interactive ? 'cursor-pointer hover:border-purple-300' : 'cursor-default'}`}
    >
      {pkg.popular && (
        <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white">
          <Crown size={10} /> Most popular
        </span>
      )}
      {interactive && selected && (
        <Check size={18} className="absolute top-3 right-3 text-purple-600" />
      )}

      <div className="flex items-center gap-3">
        <Icon size={26} className={selected ? 'text-purple-600' : 'text-gray-400'} />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-gray-800 leading-tight">{pkg.name}</div>
          {pkg.subtitle && <div className="text-xs text-gray-500">{pkg.subtitle}</div>}
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-lg font-black text-purple-600">${pkg.price}</span>
          <span className="ml-1 text-[11px] text-gray-400">CAD</span>
        </div>
      </div>

      {pkg.cardDescription && (
        <p className="mt-2.5 text-sm text-gray-600">{pkg.cardDescription}</p>
      )}

      {Array.isArray(pkg.features) && pkg.features.length > 0 && (
        <details className="mt-2 group" onClick={(e) => e.stopPropagation()}>
          <summary className="flex items-center gap-1 text-xs font-medium text-purple-600 cursor-pointer list-none select-none">
            <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
            What&apos;s included
          </summary>
          <ul className="mt-2 space-y-1.5">
            {pkg.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                <Check size={12} className="mt-0.5 flex-shrink-0 text-green-500" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default PackageCard;
